import { action, observable } from 'mobx';

import {
  Map,
  MapView,
  ScaleBar,
  CoordinateConversion,
  Home,
  LayerList,
  Expand,
  Compass,
  BasemapGallery,
  ImageryLayer
} from 'cd-eft/utils/esriTypes';
import stores from 'cd-eft/models/Stores';
import PortalBasemapsSource from '@arcgis/core/widgets/BasemapGallery/support/PortalBasemapsSource';
import BaseMap from '@arcgis/core/Basemap';
import ImageryTileLayer from '@arcgis/core/layers/ImageryTileLayer';

import { appConfig } from 'cd-eft/config/AppConfig';

export interface IMapVmProps {
  map(map: Map);
  view(view: MapView);
}

export class MapVM {
  @observable props: IMapVmProps;
  current: any;
  @observable mapView: any = '';
  goHome?: () => void;

  constructor(props: IMapVmProps) {
    this.props = props;
  }

  @action.bound async mapMount(props: IMapVmProps) {
    this.props = props;

    const map = new Map({
      basemap: /* 'topo-vector', // */ 'gray-vector',
    });

    const view = new MapView({
      container: this.current,
      map: map,
      center: [-95, 40], // CONTINENTAL US
      zoom: 3, // CONTINENTAL US
    });

    props.view(view);
    this.mapView = view;

    // const basemapGallery = new BasemapGallery({ view: view });
    
    //The following code adds a custom basemap to the basemapGallery
    //The code hides basemap "NAIP Imagery Hybrid" and then add NAIP
    // imagery of USDA( as its more updated than in the Gallery)
    let NAIPLayerTiled = new ImageryTileLayer({url: appConfig.usdaNaipSources.baseMapPath});
    let NAIPLayerNonTiled = new ImageryLayer({url: appConfig.usdaNaipSources.nonTiledPath});
    let NAIPBaseMapTiled = new BaseMap({ 
      baseLayers: [NAIPLayerTiled],
      title: "NAIP Imagery (USDA)", 
      thumbnailUrl: appConfig.usdaNaipSources.thumbnailPath
    });
    let NAIPBaseMapNonTiled = new BaseMap({
      baseLayers: [NAIPLayerNonTiled], 
      title: "NAIP Imagery (USDA)", 
      thumbnailUrl: appConfig.usdaNaipSources.thumbnailPath
    });
    let displayNAIPTiled = false;
    const portalBasemapSource = new PortalBasemapsSource({
      portal: {
        url: "https://www.arcgis.com"
      },
      filterFunction: function (basemap) {
        //hides NAIP Imagery from the Gallery
        return basemap.portalItem.title !== "NAIP Imagery Hybrid";
      },
      updateBasemapsCallback: function (items) {
        // add basemap to the array
        items.unshift(NAIPBaseMapNonTiled);
        // return the array of basemaps
        return items;
      }
    });

    let basemapGallery = new BasemapGallery({
      source: portalBasemapSource,
      view: view
    });

    const basemapGalleryWidget = new Expand({
      view: view,
      content: basemapGallery,
      expandTooltip: 'Basemap Gallery',
      collapseTooltip: 'Basemap Gallery',
    });

    view.watch('scale', (newValue) => {
      if (newValue < 4600 && displayNAIPTiled) {
        if (view.map.basemap.title === 'NAIP Imagery (USDA)') {
          view.map.basemap = NAIPBaseMapNonTiled;
        };
        displayNAIPTiled = false;
      } else if (newValue >= 4600 && !displayNAIPTiled){
        if (view.map.basemap.title === 'NAIP Imagery (USDA)') {
          view.map.basemap = NAIPBaseMapTiled;
        }
        displayNAIPTiled = true;
      }
    })

    const scaleBar = new ScaleBar({
      view: view,
      id: 'scale-ruler',
      style: 'ruler',
      unit: 'metric'
    });
    view.ui.add(scaleBar, 'bottom-left');

    const ccWidget = new CoordinateConversion({
      view: view,
      multipleConversions: false,
    });

    const homeWidget = new Home({ view: view });
    homeWidget.on('go', (e) => {
      if (this.goHome) {
        this.goHome();
      }
    });
    stores.AppStore.efh2Store.homeWidget = homeWidget;

    const layerList = new LayerList({ view: this.mapView });
    const layerListWidget = new Expand({
      view: this.mapView,
      content: layerList,
      expandTooltip: 'Layer List',
      collapseTooltip: 'Layer List',
    });

    const rotateCW = new Expand({
      expandTooltip: 'Rotate Clockwise',
      collapseTooltip: 'Rotate Clockwise',
      expandIconClass: 'esri-icon-rotate',
      collapseIconClass: 'esri-icon-rotate',
    });
    rotateCW.watch('expanded', () => {
      this.rotate3DView(-1);
    });

    const rotateCCW = new Expand({
      expandTooltip: 'Rotate Counter Clockwise',
      collapseTooltip: 'Rotate Counter Clockwise',
      expandIconClass: 'esri-icon-rotate-back',
      collapseIconClass: 'esri-icon-rotate-back',
    });
    rotateCCW.watch('expanded', () => {
      this.rotate3DView(1);
    });

    const compassWidgetA = new Compass({ view: view });
    view.ui.add({ component: compassWidgetA, position: 'top-left' });

    view.ui.add(ccWidget, 'bottom-right');
    view.ui.add({ component: basemapGalleryWidget, position: 'top-left', index: 0 });
    view.ui.add({ component: layerListWidget, position: 'top-left', index: 1 });
    view.ui.add({ component: homeWidget, position: 'top-left', index: 2 });
    view.ui.add({ component: rotateCW, position: 'top-left', index: 3 });
    view.ui.add({ component: rotateCCW, position: 'top-left', index: 4 });
  }

  @action.bound rotate3DView(direction: number) {
    const viewPoint = this.mapView.viewpoint.clone();
    let heading = viewPoint.rotation;

    // Set the heading of the view to the closest multiple of 90 degrees,
    // depending on the direction of rotation
    if (direction < 0) {
      heading = Math.floor((heading + 1e-3) / 90) * 90 + 90;
    } else {
      heading = Math.ceil((heading - 1e-3) / 90) * 90 - 90;
    }

    viewPoint.rotation = heading;

    this.mapView.goTo(viewPoint).catch((error) => {
      if (error.name != 'AbortError') {
        console.error(error);
      }
    });
  }
}
